import { useState } from 'react';
import { QueryErrorAlert, SearchBar } from '@/shared/components';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useRolesQuery } from '../hooks/useRolesQuery';
import { RolesDataTable } from '../components/RolesDataTable';

// Mirrors RoleController::MAX_LIMIT — the list endpoint is a bounded catalogue,
// never paginated, so this only decides whether to show the truncation note.
const ROLE_LIST_CAP = 100;

export function RoleList() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const query = useRolesQuery({ search: trimmedSearch || undefined });
  const roles = query.data ?? [];

  const emptyMessage = trimmedSearch ? 'No roles match your search.' : 'No roles found.';

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Roles</h1>
      </div>

      <SearchBar searchTerm={search} setSearchTerm={setSearch} placeholder="Search roles" />

      <div className="space-y-4" aria-busy={query.isFetching}>
        {query.isFetching && !query.isLoading && (
          <p role="status" className="text-sm text-mp-muted">
            Updating results...
          </p>
        )}

        {query.isError ? (
          <QueryErrorAlert message="Could not load roles." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
        ) : (
          <>
            <RolesDataTable data={roles} isLoading={query.isLoading} emptyMessage={emptyMessage} />
            {roles.length >= ROLE_LIST_CAP && (
              <p className="text-sm text-mp-muted">
                Showing the first 100 roles. Refine your search to narrow the list.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
